import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';

import {
  ClientResponseDto,
  ClientFilterDto,
  ClientPagedResponseDto,
  ClientRequestDto,
  GenericGetFilterDto,
  PagedResponse,
  ClientByOrganizationDTO,
} from '@bandido/shared/dtos';
import { QueryKeys } from '../../common/types';
import { client } from '../../common/services';

interface useGetAllClientsProps extends Partial<ClientFilterDto> {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: number;

  agencyIds?: number[];
  organizationIds?: number[];
  countries?: string[];
  cities?: string[];
  states?: string[];
  documentTypes?: string[];
  whatsappWithAi?: boolean;
  preferredAgentIds?: string[];
  sort?: string;
  order?: 'ASC' | 'DESC';

  createdAfterDate?: string;
  createdBeforeDate?: string;
  updatedAfterDate?: string;
  updatedBeforeDate?: string;
  createdDateFilter?: string;
  updatedDateFilter?: string;
  createdCustomDays?: string;
  updatedCustomDays?: string;
  createdSpecificDate?: string;
  updatedSpecificDate?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  updatedDateFrom?: string;
  updatedDateTo?: string;
}

export const useGetAllClients = (props: useGetAllClientsProps) => {
  const {
    clientName,
    currentPage,
    onErrorCallback,
    pageSize,
    search,
    agencyId,

    agencyIds,
    organizationIds,
    countries,
    cities,
    states,
    documentTypes,
    whatsappWithAi,
    preferredAgentIds,
    sort,
    order,
    createdAfterDate,
    createdBeforeDate,
    updatedAfterDate,
    updatedBeforeDate,
    createdDateFilter,
    updatedDateFilter,
    createdCustomDays,
    updatedCustomDays,
    createdSpecificDate,
    updatedSpecificDate,
    createdDateFrom,
    createdDateTo,
    updatedDateFrom,
    updatedDateTo,
  } = props;

  const apiParams: Record<string, any> = {
    currentPage: currentPage || 1,
    pageSize,
    search,
    clientName,
    agencyId,

    sort,
    order,

    agencyIds: agencyIds?.length ? agencyIds.join('|') : undefined,
    organizationIds: organizationIds?.length ? organizationIds.join('|') : undefined,
    countries: countries?.length ? countries.join('|') : undefined,
    cities: cities?.length ? cities.join('|') : undefined,
    states: states?.length ? states.join('|') : undefined,
    documentTypes: documentTypes?.length ? documentTypes.join('|') : undefined,
    preferredAgentIds: preferredAgentIds?.length ? preferredAgentIds.join('|') : undefined,

    whatsappWithAi,

    createdAfterDate,
    createdBeforeDate,
    updatedAfterDate,
    updatedBeforeDate,
    createdDateFilter,
    updatedDateFilter,
    createdCustomDays,
    updatedCustomDays,
    createdSpecificDate,
    updatedSpecificDate,
    createdDateFrom,
    createdDateTo,
    updatedDateFrom,
    updatedDateTo,
  };

  const cleanParams = Object.fromEntries(
    Object.entries(apiParams).filter(([_, value]) => value !== undefined && value !== null && value !== ''),
  );

  const queryKey = [
    QueryKeys.getAllClients,
    currentPage,
    pageSize,
    search,
    clientName,
    agencyId,
    agencyIds,
    sort,
    order,
    organizationIds,
    countries,
    cities,
    states,
    documentTypes,
    whatsappWithAi,
    preferredAgentIds,
    createdAfterDate,
    createdBeforeDate,
    updatedAfterDate,
    updatedBeforeDate,
    createdDateFilter,
    updatedDateFilter,
    createdCustomDays,
    updatedCustomDays,
    createdSpecificDate,
    updatedSpecificDate,
    createdDateFrom,
    createdDateTo,
    updatedDateFrom,
    updatedDateTo,
  ];

  return useQuery({
    queryKey,
    queryFn: async (): Promise<ClientPagedResponseDto | undefined> => {
      try {
        const clients: AxiosResponse<ClientPagedResponseDto, any> = await client.get('client', {
          params: cleanParams,
        });

        return clients.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        throw e;
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface UseGetClientById {
  id?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

interface UseGetClientByPhone {
  agencyId: number;
  phone?: string;
  whatsappNumber?: string;
}

export const useGetClientByContact = ({ agencyId, phone, whatsappNumber }: UseGetClientByPhone) =>
  useQuery({
    queryKey: [QueryKeys.getClientByPhone, { phone, agencyId }],
    queryFn: async (): Promise<AxiosResponse<ClientResponseDto, any> | undefined> =>
      client.get(`client/${agencyId}/by-contact`, {
        params: { phone, whatsappNumber },
      }),
    enabled: Boolean(phone && agencyId),
    select: (data) => data?.data,
  });

export const useGetClientById = ({ id, onErrorCallback }: UseGetClientById) =>
  useQuery({
    queryKey: [QueryKeys.getClientById, id],
    queryFn: async (): Promise<ClientResponseDto | undefined> => {
      try {
        const result: AxiosResponse<ClientResponseDto, any> = await client.get(`client/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        throw e;
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetWhatsappClientMessages = (clientId?: ClientResponseDto['id'], params?: Record<string, unknown>) =>
  useInfiniteQuery(
    [QueryKeys.getWhatsappClientMessages, { clientId, ...params }],
    ({ pageParam = 1 }) =>
      client.get(`whatsapp/${clientId}/messages`, {
        params: {
          ...params,
          page: pageParam,
        },
      }),
    {
      enabled: Boolean(clientId),
      select: (data) => ({
        pages: [...data.pages].reverse(),
        pageParams: [...data.pageParams].reverse(),
      }),
      getNextPageParam: (lastPage) => {
        if (Number(lastPage.data.currentPage) < Number(lastPage.data.pages)) {
          return Number(lastPage.data.currentPage) + 1;
        }
      },
    },
  );

interface useGetClientsByOrganizationProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  organizationId?: number;
}

export const useGetClientsByOrganization = (params: useGetClientsByOrganizationProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, organizationId } = params || {};
  return useQuery({
    enabled: Boolean(organizationId),
    queryKey: [QueryKeys.getUsersByOrganization, organizationId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<ClientByOrganizationDTO> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<ClientByOrganizationDTO>, any> = await client.get(
          `client/organization/${organizationId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface useGetUsersByAgencyProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetClientsByAgency = (params: useGetUsersByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    enabled: Boolean(agencyId),
    queryKey: [QueryKeys.getUsersByTeam, agencyId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<ClientByOrganizationDTO> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<ClientByOrganizationDTO>, any> = await client.get(
          `client/agency/${agencyId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        throw e;
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface UseToggleWhatsappWithAi {
  onSuccess?: () => void;
  onError?: (_e: AxiosError) => void;
}

export interface UpdateClientNamePayload extends Pick<Partial<ClientRequestDto>, 'firstName' | 'lastName'> {
  clientId: string;
}

interface UseUpdateClient {
  onSuccess?: (data: ClientResponseDto) => void;
  onError?: (_e: AxiosError) => void;
}

export const useToggleWhatsappWithAi = ({ onSuccess, onError }: UseToggleWhatsappWithAi) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientId: string): Promise<boolean> => {
      const response: AxiosResponse<boolean> = await client.put(`client/${clientId}/toggle-whatsapp-ai`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getClientById]);
      onSuccess?.();
    },
    onError: (error: AxiosError) => {
      onError?.(error);
    },
  });
};

export const useUpdateClient = ({ onSuccess, onError }: UseUpdateClient = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, ...payload }: UpdateClientNamePayload): Promise<ClientResponseDto> => {
      const response: AxiosResponse<ClientResponseDto> = await client.patch(`client/${clientId}`, payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QueryKeys.getClientById, variables.clientId]);
      queryClient.invalidateQueries([QueryKeys.getAllClients]);
      onSuccess?.(data);
    },
    onError: (error: AxiosError) => {
      onError?.(error);
    },
  });
};
